import {
	useCreateCheckoutSession,
	useCreateCustomerPortalSession,
	useMemberships,
	useUserProfile,
	useUserSubscription,
} from "@bodhistreams/api/src";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import FAQSection from "../components/FAQSection";
import PricingCompare from "../components/pricing/PricingCompare";
import PricingDetail from "../components/pricing/PricingDetail";
import PricingHero from "../components/pricing/PricingHero";
import "../components/pricing/pricingLayout.css";
import PricingSection from "../components/PricingSection";
import {
	COLLECTION_LABEL,
	enrichPlansWithAvailability,
	PAID_PLANS_ENABLED,
	PLAN_COLORS,
	PLAN_TAGLINES,
	planRank,
} from "../config/pricing";
import PageLayout from "./PageLayout";

const buildPlan = (membership) => {
	const key = membership.name.toLowerCase();
	const isFree = key === "free" || membership.price === 0;
	return {
		id: membership.id,
		key,
		title: membership.name,
		monthly: Number(membership.price),
		yearly: Number(membership.yearly_price || membership.price * 12),
		devices: membership.max_simultaneous_devices,
		streamingHours: membership.streaming_hours || null,
		collectionLabel: COLLECTION_LABEL[key] || "Collections",
		playlist: !isFree,
		commercial: !isFree,
		color: PLAN_COLORS[key] || "#7873f5",
		tagline: PLAN_TAGLINES[key] || "",
		membership,
	};
};

const Pricing = () => {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const planParam = (searchParams.get("plan") || "").toLowerCase();

	const [yearlyBilling, setYearlyBilling] = useState(false);
	const [processingPlanId, setProcessingPlanId] = useState(null);

	const { data: membershipsData } = useMemberships();
	const memberships = Array.isArray(membershipsData) ? membershipsData : [];
	const { data: user } = useUserProfile();
	const { data: userSubscription } = useUserSubscription(Boolean(user));
	const createCheckoutSessionMutation = useCreateCheckoutSession();
	const createPortalSessionMutation = useCreateCustomerPortalSession();

	const plans = useMemo(
		() =>
			enrichPlansWithAvailability(
				memberships
					.map(buildPlan)
					.sort((a, b) => planRank(a.key) - planRank(b.key)),
			),
		[memberships],
	);

	const selectedPlan = useMemo(() => {
		if (!plans.length) return null;
		return (
			plans.find((p) => p.key === planParam) ||
			plans.find((p) => !p.disabled) ||
			plans[0]
		);
	}, [plans, planParam]);

	useEffect(() => {
		if (selectedPlan && planParam !== selectedPlan.key) {
			setSearchParams({ plan: selectedPlan.key }, { replace: true });
		}
	}, [selectedPlan, planParam, setSearchParams]);

	const handleSelectPlan = (planKey) => {
		const plan = plans.find((p) => p.key === planKey);
		if (plan?.disabled) return;
		setSearchParams({ plan: planKey }, { replace: false });
	};

	const handleSubscribe = async () => {
		if (!selectedPlan || selectedPlan.disabled) return;
		const membership = selectedPlan.membership;

		if (selectedPlan.key === "free" || selectedPlan.monthly === 0) {
			navigate("/login");
			return;
		}

		setProcessingPlanId(membership.id);
		try {
			if (userSubscription?.stripe_subscription_id) {
				const response = await createPortalSessionMutation.mutateAsync();
				if (response?.portal_url) {
					window.location.href = response.portal_url;
				} else {
					window.alert("Failed to get subscription management URL");
				}
				return;
			}

			const response = await createCheckoutSessionMutation.mutateAsync({
				membershipId: membership.id,
				isYearly: yearlyBilling,
			});
			if (response.checkout_url) {
				window.location.href = response.checkout_url;
			}
		} catch (error) {
			window.alert(error.message || "Failed to create checkout session");
			if (
				error.message?.includes("authentication") ||
				error.message?.includes("login")
			) {
				navigate("/login");
			}
		} finally {
			setProcessingPlanId(null);
		}
	};

	const isMutating =
		createCheckoutSessionMutation.isPending ||
		createPortalSessionMutation.isPending;
	const isProcessing =
		isMutating && processingPlanId === selectedPlan?.membership?.id;

	const isLoggedIn = Boolean(user);
	const hasActiveSubscription = Boolean(
		userSubscription?.stripe_subscription_id,
	);

	let primaryLabel = "Subscribe Now";
	if (selectedPlan?.disabled) primaryLabel = "Coming Soon";
	else if (selectedPlan?.key === "free") primaryLabel = "Get Started Free";
	else if (hasActiveSubscription) primaryLabel = "Manage Subscription";
	else if (!isLoggedIn) primaryLabel = "Sign Up & Subscribe";

	const detailPrice = selectedPlan
		? yearlyBilling
			? selectedPlan.yearly
			: selectedPlan.monthly
		: 0;
	const savings = selectedPlan
		? selectedPlan.monthly * 12 - selectedPlan.yearly
		: 0;
	const showSavings =
		yearlyBilling && selectedPlan && selectedPlan.yearly > 0 && savings > 0;

	return (
		<PageLayout>
			<div className="pricing-page">
				<div className="pricing-page-inner">
					<PricingHero
						title="Choose Your Plan"
						subtitle={
							PAID_PLANS_ENABLED
								? "Pick the membership that fits your listening habits. Switch or cancel any time."
								: "Explore our wellness music collections. Starting for free."
						}
					/>

					<PricingSection
						selectedPlanKey={selectedPlan?.key}
						onSelectPlan={handleSelectPlan}
						yearlyBilling={yearlyBilling}
						onToggleBilling={setYearlyBilling}
					/>

					{selectedPlan && (
						<PricingDetail
							selectedPlan={selectedPlan}
							yearlyBilling={yearlyBilling}
							detailPrice={detailPrice}
							savings={savings}
							showSavings={showSavings}
							primaryLabel={primaryLabel}
							isProcessing={isProcessing}
							isLoggedIn={isLoggedIn}
							paidPlansEnabled={PAID_PLANS_ENABLED}
							onSubscribe={handleSubscribe}
							onLogin={() => navigate("/login")}
						/>
					)}

					<PricingCompare
						plans={plans}
						selectedPlanKey={selectedPlan?.key}
						yearlyBilling={yearlyBilling}
					/>

					<FAQSection />
				</div>
			</div>
		</PageLayout>
	);
};

export default Pricing;
